import React, { useMemo, useState } from "react";
import {
  Combobox as HeadlessCombobox,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import { CheckIcon, RefreshIcon, XIcon } from "../../icons";

export type ComboboxListItem<T extends string = string> = {
  key: T;
  text: React.ReactNode;
  searchValue?: string;
  unavailable?: boolean;
};

type ComboboxProps<T extends string> = {
  label?: React.ReactNode;
  labelAccessory?: React.ReactNode;
  value: T | "";
  onChange: (value: T | "") => void;
  items: ComboboxListItem<T>[];
  allowCustomValue?: boolean;
  className?: string;
  disabled?: boolean;
  subtext?: string;
  placeholder?: string;
  isLoading?: boolean;
  loadingText?: string;
  emptyText?: string;
  onRefresh?: () => void;
  refreshLabel?: string;
  clearLabel?: string;
  customValueLabel?: (value: string) => React.ReactNode;
};

const DEFAULT_PLACEHOLDER = "Select or type an option";
const DEFAULT_LOADING_TEXT = "Loading...";
const DEFAULT_EMPTY_TEXT = "No matches found.";
const DEFAULT_REFRESH_LABEL = "Refresh options";
const DEFAULT_CLEAR_LABEL = "Clear selection";

function getItemSearchValue<T extends string>(item: ComboboxListItem<T>) {
  return `${item.searchValue ?? ""} ${item.key} ${typeof item.text === "string" ? item.text : ""}`.toLowerCase();
}

const Combobox = <T extends string>({
  label,
  labelAccessory,
  value,
  onChange,
  items,
  allowCustomValue = false,
  className = "",
  disabled = false,
  subtext,
  placeholder = DEFAULT_PLACEHOLDER,
  isLoading = false,
  loadingText = DEFAULT_LOADING_TEXT,
  emptyText = DEFAULT_EMPTY_TEXT,
  onRefresh,
  refreshLabel = DEFAULT_REFRESH_LABEL,
  clearLabel = DEFAULT_CLEAR_LABEL,
  customValueLabel = (customValue) => customValue,
}: ComboboxProps<T>) => {
  const [query, setQuery] = useState("");
  const descriptionId = React.useId();
  const hasText = value !== "" || query !== "";

  const filteredItems = useMemo(() => {
    if (query === "") {
      return items;
    }

    const normalizedQuery = query.toLowerCase();
    return items.filter((item) => getItemSearchValue(item).includes(normalizedQuery));
  }, [items, query]);

  const setValue = (nextValue: string | null) => {
    onChange((nextValue ?? "") as T | "");
  };

  const clearValue = () => {
    setQuery("");
    onChange("");
  };

  return (
    <div className={className}>
      <HeadlessCombobox<string>
        immediate
        value={value as string}
        onChange={(nextValue) => {
          setValue(nextValue);
          setQuery("");
        }}
        disabled={disabled}
      >
        {(label || labelAccessory) && (
          <div className="mb-1 flex items-center gap-2">
            {label && <ComboboxLabel className="block text-sm font-medium text-gray-700">{label}</ComboboxLabel>}
            {labelAccessory}
          </div>
        )}
        <div className="relative">
          <ComboboxInput
            displayValue={(selectedValue: string) => selectedValue ?? ""}
            onChange={(event) => {
              const nextQuery = event.target.value;
              setQuery(nextQuery);
              if (allowCustomValue) {
                onChange(nextQuery as T | "");
              }
            }}
            placeholder={placeholder}
            className="
              block w-full rounded-md border border-gray-300 py-2 pr-20 pl-3 text-sm shadow-sm
              placeholder:text-gray-300
              focus:border-primary-500 focus:ring-primary-500
              disabled:bg-gray-50 disabled:text-gray-500
            "
          />
          <div className="absolute inset-y-0 right-0 flex items-center">
            {hasText && !disabled && (
              <button
                type="button"
                aria-label={clearLabel}
                onMouseDown={(event) => event.preventDefault()}
                onClick={clearValue}
                className="
                  flex h-full items-center px-2 text-gray-400
                  hover:text-gray-600
                  focus:outline-none
                "
              >
                <XIcon className="size-4" />
              </button>
            )}
            {onRefresh && (
              <button
                type="button"
                aria-label={refreshLabel}
                disabled={disabled || isLoading}
                onMouseDown={(event) => event.preventDefault()}
                onClick={onRefresh}
                className="
                  flex h-full items-center px-3 text-gray-400
                  hover:text-gray-600
                  focus:outline-none
                  disabled:cursor-not-allowed disabled:text-gray-300
                "
              >
                <RefreshIcon className={`size-4 ${isLoading ? "animate-spin" : ""}`} />
              </button>
            )}
          </div>

          <ComboboxOptions
            modal={false}
            // Float in a portal anchored to the input so the list escapes any overflow-hidden ancestor.
            anchor="bottom start"
            className="
              z-10 max-h-56 w-[var(--input-width)] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg
              ring-1 ring-black/5
              [--anchor-gap:4px]
              empty:invisible
            "
          >
            {isLoading ? (
              <div className="px-3 py-2 text-gray-500">{loadingText}</div>
            ) : (
              <>
                {allowCustomValue && query.length > 0 && (
                  <ComboboxOption
                    value={query}
                    className="
                      group flex cursor-pointer items-center px-3 py-2 text-gray-900
                      data-focus:bg-gray-50
                    "
                  >
                    <span className="block truncate">{customValueLabel(query)}</span>
                    {value === query && <CheckIcon className="ml-auto size-5 shrink-0 text-emerald-600" />}
                  </ComboboxOption>
                )}

                {filteredItems.length === 0 ? (
                  <div className="px-3 py-2 text-gray-500">{emptyText}</div>
                ) : (
                  filteredItems.map((item) => (
                    <ComboboxOption
                      key={item.key}
                      value={item.key}
                      disabled={item.unavailable}
                      className="
                        group flex cursor-pointer items-center px-3 py-2 text-gray-900
                        data-disabled:cursor-not-allowed data-disabled:opacity-50
                        data-focus:bg-gray-50
                      "
                    >
                      <span className="block truncate">{item.text}</span>
                      {value === item.key && <CheckIcon className="ml-auto size-5 shrink-0 text-emerald-600" />}
                    </ComboboxOption>
                  ))
                )}
              </>
            )}
          </ComboboxOptions>
        </div>
      </HeadlessCombobox>
      {subtext && (
        <p className="mt-2 whitespace-pre-line text-xs text-gray-400" id={descriptionId}>
          {subtext}
        </p>
      )}
    </div>
  );
};

export default Combobox;
